import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, BarChart3, ChevronRight, Search, X, Download } from "lucide-react";

const fmt = (val, currency = "CHF") => {
    const n = Number(val || 0);
    return `${currency} ${n.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return s; }
};

export default function StatsPage() {
    const [trips, setTrips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState("date_desc");
    const [query, setQuery] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/trips");
            setTrips(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const sorted = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filtered = q
            ? trips.filter((t) => {
                const hay = [t.title, t.client_name, t.destination]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return hay.includes(q);
            })
            : trips;
        const arr = [...filtered];
        const k = sortBy;
        arr.sort((a, b) => {
            if (k === "date_desc") return (b.start_date || "").localeCompare(a.start_date || "");
            if (k === "date_asc") return (a.start_date || "").localeCompare(b.start_date || "");
            if (k === "profit_desc") return (b.profit || 0) - (a.profit || 0);
            if (k === "profit_asc") return (a.profit || 0) - (b.profit || 0);
            if (k === "sale_desc") return (b.total_cost || 0) - (a.total_cost || 0);
            return 0;
        });
        return arr;
    }, [trips, sortBy, query]);

    const totals = useMemo(() => {
        const acc = { sale: 0, cost: 0, profit: 0, count: sorted.length };
        for (const t of sorted) {
            acc.sale += t.total_cost || 0;
            acc.cost += t.internal_cost || 0;
            acc.profit += t.profit || 0;
        }
        acc.margin = acc.sale > 0 ? (acc.profit / acc.sale) * 100 : 0;
        return acc;
    }, [sorted]);

    if (loading) return <AppLayout><div className="text-sm text-ink-tertiary">Laden …</div></AppLayout>;

    const exportCSV = () => {
        const sep = ";"; // Excel-DE/CH default
        const escape = (val) => {
            const s = val == null ? "" : String(val);
            return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const num = (val) => {
            const n = Number(val || 0);
            // Swiss/German format: comma as decimal separator
            return n.toFixed(2).replace(".", ",");
        };
        const headers = [
            "Reise", "Kunde", "Ort", "Anreise", "Abreise",
            "Währung", "Einkauf", "Verkauf", "Gewinn", "Marge %", "Status",
        ];
        const rows = sorted.map((t) => [
            t.title || "",
            t.client_name || "",
            t.destination || "",
            t.start_date || "",
            t.end_date || "",
            t.currency || "CHF",
            num(t.internal_cost),
            num(t.total_cost),
            num(t.profit),
            num(t.profit_margin),
            t.status || "",
        ]);
        const csv = [headers, ...rows].map((r) => r.map(escape).join(sep)).join("\r\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
        const ts = new Date().toISOString().slice(0, 10);
        const suffix = query ? `_${query.replace(/[^a-zA-Z0-9äöüÄÖÜ_-]/g, "")}` : "";
        const filename = `pro-travel-statistik_${ts}${suffix}.csv`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`${rows.length} ${rows.length === 1 ? "Reise" : "Reisen"} als CSV exportiert`);
    };

    return (
        <AppLayout>
            <div className="max-w-7xl">
                <p className="text-xs uppercase tracking-[0.18em] text-ink-tertiary mb-3">Übersicht</p>
                <h1 className="text-4xl font-bold mb-2 text-ink" style={{ fontFamily: "Space Grotesk" }}>
                    Reise <span className="italic font-normal" style={{ fontFamily: "Fraunces" }}>Statistik</span>
                </h1>
                <p className="text-sm text-ink-secondary mb-8">
                    Alle Reisen auf einen Blick — mit Verkaufspreis, Einkaufskosten und Gewinn pro Reise.
                </p>

                {/* Summary cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <SummaryCard label="Reisen" value={totals.count} testid="stats-count" />
                    <SummaryCard label="Verkauf gesamt" value={fmt(totals.sale)} testid="stats-sale" />
                    <SummaryCard label="Einkauf gesamt" value={fmt(totals.cost)} muted testid="stats-cost" />
                    <SummaryCard
                        label={`Gewinn (${totals.margin.toFixed(1)}%)`}
                        value={fmt(totals.profit)}
                        accent={totals.profit >= 0 ? "positive" : "negative"}
                        testid="stats-profit"
                    />
                </div>

                {/* Toolbar: Suche + Sortierung */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
                        <div className="relative flex-1 min-w-[240px] max-w-md">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary pointer-events-none" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Suche nach Reise, Kunde oder Ort …"
                                data-testid="stats-search"
                                className="w-full bg-white border border-sand-300 rounded-md pl-9 pr-9 py-2 text-sm text-ink placeholder:text-ink-tertiary focus:outline-none focus:ring-2 focus:ring-navy"
                            />
                            {query && (
                                <button
                                    type="button"
                                    onClick={() => setQuery("")}
                                    data-testid="stats-search-clear"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-sand-200 text-ink-tertiary hover:text-ink transition-colors"
                                    aria-label="Suche zurücksetzen"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-2 text-ink-secondary whitespace-nowrap">
                            <BarChart3 className="w-4 h-4" />
                            <span className="text-sm" data-testid="stats-result-count">
                                {sorted.length} {sorted.length === 1 ? "Reise" : "Reisen"}
                                {query && trips.length !== sorted.length && (
                                    <span className="text-ink-tertiary"> von {trips.length}</span>
                                )}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={exportCSV}
                            disabled={sorted.length === 0}
                            data-testid="stats-export-csv"
                            className="inline-flex items-center gap-1.5 bg-ink text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-navy disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            title="Gefilterte Reisen als CSV (Excel) exportieren"
                        >
                            <Download className="w-4 h-4" />
                            <span className="hidden sm:inline">CSV Export</span>
                        </button>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            data-testid="stats-sort"
                            className="bg-white border border-sand-300 rounded-md px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-navy"
                        >
                            <option value="date_desc">Neueste zuerst</option>
                            <option value="date_asc">Älteste zuerst</option>
                            <option value="profit_desc">Höchster Gewinn</option>
                            <option value="profit_asc">Niedrigster Gewinn</option>
                            <option value="sale_desc">Höchster Verkauf</option>
                        </select>
                    </div>
                </div>

                {/* Table */}
                <div className="pt-card overflow-hidden" data-testid="stats-table">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-sand-deep border-b border-sand-300">
                                <tr>
                                    <Th>Reise</Th>
                                    <Th>Kunde</Th>
                                    <Th>Ort</Th>
                                    <Th>Datum</Th>
                                    <Th align="right">Einkauf</Th>
                                    <Th align="right">Verkauf</Th>
                                    <Th align="right">Gewinn</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.length === 0 && (
                                    <tr><td colSpan={7} className="text-center text-ink-tertiary py-10">
                                        {query ? `Keine Reise gefunden für „${query}".` : "Noch keine Reisen vorhanden."}
                                    </td></tr>
                                )}
                                {sorted.map((t) => {
                                    const profit = t.profit ?? 0;
                                    const margin = t.profit_margin ?? 0;
                                    const positive = profit >= 0;
                                    return (
                                        <tr key={t.id} className="border-b border-sand-300 hover:bg-sand-100 transition-colors" data-testid={`stats-row-${t.id}`}>
                                            <Td>
                                                <Link
                                                    to={`/trips/${t.id}`}
                                                    className="font-semibold text-ink hover:text-navy hover:underline underline-offset-4 decoration-navy/40 transition-colors"
                                                    data-testid={`stats-trip-link-${t.id}`}
                                                >
                                                    {t.title || "—"}
                                                </Link>
                                            </Td>
                                            <Td>
                                                {t.client_name ? (
                                                    <Link
                                                        to={`/trips/${t.id}`}
                                                        className="text-ink hover:text-navy hover:underline underline-offset-4 decoration-navy/40 transition-colors"
                                                        data-testid={`stats-client-link-${t.id}`}
                                                    >
                                                        {t.client_name}
                                                    </Link>
                                                ) : (
                                                    <span className="text-ink-tertiary">—</span>
                                                )}
                                            </Td>
                                            <Td className="text-ink-secondary">{t.destination || "—"}</Td>
                                            <Td className="text-ink-secondary whitespace-nowrap">{fmtDate(t.start_date)} – {fmtDate(t.end_date)}</Td>
                                            <Td align="right" className="text-ink-secondary tabular-nums">{fmt(t.internal_cost, t.currency)}</Td>
                                            <Td align="right" className="font-semibold text-ink tabular-nums">{fmt(t.total_cost, t.currency)}</Td>
                                            <Td align="right">
                                                <div className={`flex items-center justify-end gap-1.5 font-semibold tabular-nums ${positive ? "text-emerald-700" : "text-red-700"}`}>
                                                    {positive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                                                    <span>{fmt(profit, t.currency)}</span>
                                                    <span className="text-[10px] font-normal text-ink-tertiary">({margin.toFixed(0)}%)</span>
                                                </div>
                                            </Td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}

function Th({ children, align = "left" }) {
    return (
        <th className={`text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary px-4 py-3 text-${align}`}>{children}</th>
    );
}
function Td({ children, align = "left", className = "" }) {
    return (
        <td className={`px-4 py-3 align-middle text-${align} ${className}`}>{children}</td>
    );
}

function SummaryCard({ label, value, accent, muted, testid }) {
    let valueColor = "text-ink";
    let bg = "bg-white";
    if (accent === "positive") { valueColor = "text-emerald-700"; bg = "bg-emerald-50/60 border-emerald-200"; }
    else if (accent === "negative") { valueColor = "text-red-700"; bg = "bg-red-50/60 border-red-200"; }
    if (muted) valueColor = "text-ink-secondary";
    return (
        <div className={`pt-card ${bg} px-5 py-4`} data-testid={testid}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-tertiary mb-1">{label}</p>
            <p className={`text-2xl font-bold ${valueColor}`} style={{ fontFamily: "Space Grotesk" }}>{value}</p>
        </div>
    );
}
